// OBT F3-D — Varlığa geçmiş kaydı ekleme diyaloğu (append-only; opsiyonel foto).
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { ASSET_EVENT_CHOICES } from './memoryEnums';

const ALLOWED_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_BYTES = 10 * 1024 * 1024;

export default function AddRecordDialog({ open, asset, onClose, onAdd, error = null }) {
  const [form, setForm] = useState({});
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ eventType: 'maintenance' });
      setFiles([]);
      setFileError(null);
      setSubmitting(false);
    }
  }, [open]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    for (const f of picked) {
      if (!ALLOWED_MIME.includes(f.type)) {
        setFileError('Yalnız JPEG/PNG/WebP fotoğraf yüklenebilir.');
        return;
      }
      if (f.size > MAX_BYTES) {
        setFileError('Fotoğraf 10 MB sınırını aşıyor.');
        return;
      }
    }
    setFileError(null);
    setFiles(picked);
  };

  const valid = Boolean(form.eventType) && !fileError;

  const handleConfirm = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await onAdd({ fields: form, files });
      onClose();
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Geçmiş Kaydı Ekle</DialogTitle>
          <DialogDescription>
            {asset ? `"${asset.name || asset.asset_type}" varlığına ` : ''}
            yeni bir olay kaydı ekleyin. Kayıtlar değiştirilemez (append-only);
            düzeltme için yeni kayıt eklenir.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rec-event">Olay tipi</Label>
              <select
                id="rec-event"
                className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                value={form.eventType || 'maintenance'}
                onChange={set('eventType')}
                disabled={submitting}
              >
                {ASSET_EVENT_CHOICES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rec-date">Yapım / değişim tarihi</Label>
              <Input id="rec-date" type="date" value={form.eventDate || ''} onChange={set('eventDate')} disabled={submitting} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rec-company">Firma</Label>
              <Input id="rec-company" value={form.companyName || ''} maxLength={200} onChange={set('companyName')} disabled={submitting} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rec-tender">İhale numarası</Label>
              <Input id="rec-tender" value={form.tenderNumber || ''} maxLength={64} placeholder="Ör. 2024/118" onChange={set('tenderNumber')} disabled={submitting} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rec-wstart">Garanti başlangıcı</Label>
              <Input id="rec-wstart" type="date" value={form.warrantyStart || ''} onChange={set('warrantyStart')} disabled={submitting} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rec-wend">Garanti bitişi</Label>
              <Input id="rec-wend" type="date" value={form.warrantyEnd || ''} onChange={set('warrantyEnd')} disabled={submitting} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rec-desc">Açıklama</Label>
            <Textarea id="rec-desc" rows={2} maxLength={2000} value={form.description || ''} onChange={set('description')} disabled={submitting} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rec-notes">Notlar</Label>
            <Textarea id="rec-notes" rows={2} maxLength={4000} value={form.notes || ''} onChange={set('notes')} disabled={submitting} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rec-files">Fotoğraflar (opsiyonel)</Label>
            <input
              id="rec-files"
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              className="block text-xs"
              onChange={onPickFiles}
              disabled={submitting}
            />
            {files.length > 0 && <p className="text-xs text-gray-500">{files.length} fotoğraf seçildi</p>}
            {fileError && <p role="alert" className="text-sm text-red-600">{fileError}</p>}
          </div>
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Vazgeç</Button>
          <Button onClick={handleConfirm} disabled={!valid || submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            Kaydı Ekle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
